import { useMutation, useQuery } from "@tanstack/react-query";
import { Camera, Loader2, ScanLine } from "lucide-react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import {
  sgtinReplacePrompt,
  sgtinReplaceScan,
  sgtinReplaceTarget,
  type SgtinReplaceTarget,
} from "@/lib/fbs";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { useScanCapture } from "@/lib/use-scan-capture";

/** Кнопка «Заменить товар» — открывает диалог замены (пока он открыт, экран
 *  сбора обязан выключить свой перехват скана: см. `onOpenChange`). */
export function ReplaceSgtinButton({
  row,
  disabled,
  onOpenChange,
  onReplaced,
}: {
  row: Parameters<typeof sgtinReplaceTarget>[0];
  disabled?: boolean;
  /** Экран сбора гасит основной перехват скана, пока диалог открыт. */
  onOpenChange?: (open: boolean) => void;
  onReplaced: (row: fbs.IssueRow) => void;
}): React.ReactElement {
  // Чанк камеры — заранее, пока кнопка на экране: не по тапу (см. lazy-chunk).
  usePreloadChunk(CameraScanner);
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const toggle = (v: boolean) => {
    openRef.current = v;
    setOpen(v);
    onOpenChange?.(v);
  };
  // Строка могла исчезнуть с открытым диалогом (статус у WB сменился, опрос
  // перечитал список) — экран сбора не должен остаться с выключенным сканом.
  useEffect(
    () => () => {
      if (openRef.current) onOpenChangeRef.current?.(false);
    },
    [],
  );
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="text-sky-800"
        onClick={() => toggle(true)}
        disabled={disabled}
      >
        Заменить товар
      </Button>
      {open && (
        <ReplaceSgtinDialog
          target={sgtinReplaceTarget(row)}
          onClose={() => toggle(false)}
          onReplaced={(r) => {
            toggle(false);
            onReplaced(r);
          }}
        />
      )}
    </>
  );
}

/** «Заменить товар»: штуку с забракованным КИЗ вынимают из короба и сканируют
 *  ДРУГОЙ экземпляр того же товара — его КИЗ уходит в то же задание WB.
 *  Стикер и короб прежние, печати нет.
 *
 *  Скан ловится тем же способом, что основной на экране сбора: невидимое поле
 *  сканера (`useScanCapture`) и камера, если она разрешена инсталляции. Поле
 *  диалога забирает фокус при открытии — иначе код ушёл бы в поле экрана
 *  сбора (оно тоже «текстовое», и перехват диалога его фокус не отнимает).
 *  Ошибки сервера показываются в диалоге целиком, не тостом. */
export function ReplaceSgtinDialog({
  target,
  onClose,
  onReplaced,
}: {
  target: SgtinReplaceTarget;
  onClose: () => void;
  onReplaced: (row: fbs.IssueRow) => void;
}): React.ReactElement {
  const configQ = useQuery({
    queryKey: ["fbs-config"],
    queryFn: fbs.getConfig,
    staleTime: 300000,
  });
  const scanMethod = configQ.data?.scan_method ?? "tsd";
  const cameraAllowed = scanMethod === "camera" || scanMethod === "both";
  const [cameraOpen, setCameraOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Замена прошла, но о WB есть что сказать (прежний срок годности остался в
  // задании): диалог не закрывается сам — человек читает и подтверждает.
  const [done, setDone] = useState<fbs.SgtinReplaceOut | null>(null);

  const mut = useMutation({
    mutationFn: (body: fbs.SgtinReplaceRequest) => fbs.replaceSgtin(target.pickId, body),
    onSuccess: (row) => {
      toast.success(
        row.issue === "no_partia"
          ? "КИЗ заменён, проверка WB идёт. Партия новой штуки не определилась — она в «Разборе»"
          : "КИЗ заменён, проверка WB идёт",
      );
      if (row.warning) {
        setDone(row);
        return;
      }
      onReplaced(row);
    },
    onError: (e) => setError(apiErrorText(e, "Заменить товар не удалось")),
  });

  const onScan = (raw: string) => {
    const r = sgtinReplaceScan(raw);
    if ("error" in r) {
      setError(r.error);
      return;
    }
    setError(null);
    mut.mutate(r.body);
  };

  const { scanInputRef, scanInputProps } = useScanCapture({
    enabled: !mut.isPending && !cameraOpen && !done,
    onScan,
    onBlocked: () =>
      setError(
        mut.isPending
          ? "Предыдущий скан ещё отправляется — дождитесь ответа."
          : "Закройте камеру, потом сканируйте сканером.",
      ),
  });
  // Фокус — полю диалога сразу: у поля экрана сбора его перехват не отнимет.
  useEffect(() => {
    scanInputRef.current?.focus({ preventScroll: true });
  }, [scanInputRef]);

  const close = () => {
    if (mut.isPending) return;
    // Замена уже сделана — экран сбора обязан перечитать позицию.
    if (done) onReplaced(done);
    else onClose();
  };
  useBackGuard(true, close);
  useBackGuard(cameraOpen, () => setCameraOpen(false));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4"
      onClick={close}
    >
      <input
        ref={scanInputRef}
        {...scanInputProps}
        className="pointer-events-none fixed left-0 top-0 size-px opacity-0"
      />
      <div
        role="dialog"
        aria-label="Заменить товар"
        className="w-full max-w-sm rounded-2xl bg-white p-3.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[16px] font-bold">Заменить товар</div>
        <p className="mt-1 text-[13px] text-slate-800">{sgtinReplacePrompt(target)}</p>
        {done ? (
          <p role="status" className="mt-3 whitespace-pre-line rounded-lg bg-amber-50 px-3 py-2 text-[13px] font-medium text-amber-900">
            КИЗ заменён. {done.warning}
          </p>
        ) : (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">
            {mut.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" /> Отправляю новый КИЗ в WB…
              </>
            ) : (
              <>
                <ScanLine className="size-4" /> Жду скан кода маркировки
              </>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="mt-2 whitespace-pre-line rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-800">
            {error}
          </p>
        )}
        <div className="mt-3 flex gap-2">
          {done ? (
            <Button className="h-12 flex-1" onClick={close}>
              Понятно
            </Button>
          ) : (
            <Button variant="outline" className="h-12 flex-1" onClick={close} disabled={mut.isPending}>
              Отмена
            </Button>
          )}
          {cameraAllowed && !done && (
            <Button
              className="h-12 flex-1 bg-sky-700 hover:bg-sky-800"
              onClick={() => setCameraOpen(true)}
              disabled={mut.isPending}
            >
              <Camera className="mr-1 size-4" /> Камерой
            </Button>
          )}
        </div>
      </div>
      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-[80] bg-black" onClick={(e) => e.stopPropagation()}>
            <CameraScanner
              onDetect={(raw) => {
                setCameraOpen(false);
                onScan(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}
